import React, { Component, DetailedHTMLProps, HTMLAttributes } from 'react'
import { uniqueId } from 'lodash/fp'

import { ResponsiveElement } from 'components/utils/responsiveUtils'

import {
  root,
  heightReferenceElement,
  contentContainer,
  innerContainer,
  buttonContainer,
  button,
} from './index.module.scss'

interface Props
  extends DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement> {
  buttonText: {
    readMore: string
    readLess: string
  }
}

interface State {
  isCollapsed: boolean
  heightReference?: number
  innerContainerHeight?: number
  buttonContainerHeight?: number
}

export default class ExpandableText extends Component<Props, State> {
  private uniqueId: string

  state: State = {
    isCollapsed: true,
    heightReference: undefined,
    innerContainerHeight: undefined,
    buttonContainerHeight: undefined,
  }

  constructor(props: Props) {
    super(props)
    this.uniqueId = uniqueId('id-')
  }

  onHeightReferenceSizeChange = ({ height }: { height: number }) => {
    this.setState({
      heightReference: height,
    })
  }

  onInnerContainerSizeChange = ({ height }: { height: number }) => {
    this.setState({
      innerContainerHeight: height,
    })
  }

  onButtonContainerSizeChange = ({ height }: { height: number }) => {
    this.setState({
      buttonContainerHeight: height,
    })
  }

  handleButtonClick = () => {
    if (this.isOverflowing()) {
      this.setState({ isCollapsed: !this.state.isCollapsed })
    }
  }

  getProposedContentHeight = () => {
    const { heightReference, buttonContainerHeight } = this.state
    if (heightReference === undefined || buttonContainerHeight === undefined) {
      return 0
    }
    return heightReference - buttonContainerHeight
  }

  isOverflowing = () => {
    const { innerContainerHeight } = this.state
    if (innerContainerHeight === undefined) {
      return false
    }
    return innerContainerHeight > this.getProposedContentHeight()
  }

  render() {
    const {
      children,
      className,
      buttonText: { readMore, readLess },
    } = this.props
    const {
      isCollapsed,
      heightReference,
      innerContainerHeight,
      buttonContainerHeight,
    } = this.state
    const fullyInitialized =
      heightReference !== undefined &&
      innerContainerHeight !== undefined &&
      buttonContainerHeight !== undefined
    const isOverflowing = this.isOverflowing()
    const proposedContentHeight = this.getProposedContentHeight()
    const actualContentHeight = this.state.innerContainerHeight
    const maxHeight = !fullyInitialized
      ? undefined
      : isCollapsed === true
      ? proposedContentHeight
      : actualContentHeight

    const resetHeightStyles = fullyInitialized
      ? { height: 'auto', maxHeight: 'none' }
      : {}

    return (
      <div
        className={`${className} ${root}`}
        style={resetHeightStyles}
        id={this.uniqueId}
        data-overflowing={isOverflowing}
        data-fully-initialized={fullyInitialized}
      >
        <ResponsiveElement
          className={`${className} ${heightReferenceElement}`}
          aria-hidden="true"
          onSizeChange={this.onHeightReferenceSizeChange}
          metricsToListenFor={['height']}
        />
        <div className={contentContainer} style={{ maxHeight }}>
          <ResponsiveElement
            className={innerContainer}
            onSizeChange={this.onInnerContainerSizeChange}
            metricsToListenFor={['height']}
          >
            {children}
          </ResponsiveElement>
        </div>
        <ResponsiveElement
          className={buttonContainer}
          aria-hidden={!isOverflowing}
          onSizeChange={this.onButtonContainerSizeChange}
          metricsToListenFor={['height']}
        >
          <button
            onClick={this.handleButtonClick}
            className={button}
            aria-expanded={isOverflowing ? !isCollapsed : undefined}
            aria-controls={this.uniqueId}
          >
            {isCollapsed ? readMore : readLess}
          </button>
        </ResponsiveElement>
      </div>
    )
  }
}
